import { MouseEvent, useCallback, useMemo } from 'react';
import { Form, FormRenderProps } from 'react-final-form';
import { connect, useDispatch } from 'react-redux';
import type { Dispatch } from 'redux';
import { isEmpty } from 'ramda';

import { Button } from 'UIPackage/MainModule/components/Button';
import { DatePickerField } from 'UIPackage/MainModule/components/DatePickerField';
import { Dropdown } from 'UIPackage/MainModule/components/Dropdown';
import { IDropdownItem } from 'UIPackage/MainModule/interfaces/IDropdownItem';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { ExportFormatsEnum } from 'APIPackage/ImportExportModule/enums/ExportFormatsEnum';
import { DATE_FORMAT_LONG_FNS } from 'CorePackage/MainModule/constants/dateFormats';
import { makeNoLaterThenValidator } from 'CorePackage/MainModule/validators/isNoLaterThen';
import { makeNoSoonerThenValidator } from 'CorePackage/MainModule/validators/isNoSoonerThan';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { IKnmKind } from 'APIPackage/DictionariesModule/interfaces/IKnmKind';
import { IDictionarySelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionarySelectOption';
import {
  getCatalogsAnalyticsTerritorialUnitsSelectOptions,
  getCatalogsProsecutorOrganizationsSelectOptions,
  getCatalogsTerritorialUnitsSelectOptionsByLevelsCountryFederalDistrictRegions,
} from 'CorePackage/CatalogsModule/selectors/catalogsSelectors';
import { ITerritorialUnitSelectOption } from 'CorePackage/CatalogsModule/interfaces/ITerritorialUnitSelectOption';
import { IUserOrganisationSelectOption } from 'CorePackage/CatalogsModule/interfaces/IUserOrganisationSelectOption';
import { CheckboxField } from 'UIPackage/MainModule/components/CheckboxField';
import { formatDate } from 'CorePackage/MainModule/common/formatDate';
import { DropdownItem } from 'UIPackage/MainModule/components/DropdownItem';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { Row } from 'UIPackage/FormModule/components/Row';
import { ModalActions } from 'UIPackage/ModalModule/components/ModalActions';
import { IProsecutorOrganizationsSelectOption } from 'CorePackage/CatalogsModule/interfaces/IProsecutorOrganizationsSelectOption';
import { getAccountKnoOrganizationsSelectOptions } from 'CorePackage/AccountModule/selectors/accountSelectors';
import { getUserRoleIsSupervisor } from 'AuthPackage/MainModule/selectors/authSelectors';
import { IAnalyticsTerritorialUnitSelectOption } from 'CorePackage/CatalogsModule/interfaces/IAnalyticsTerritorialUnitSelectOption';

import { IErknmAnalyticsFilter } from '../interfaces/IErknmAnalyticsFilter';
import { getErknmAnalyticsFilter, getErknmAnalyticsKnmKindSelectOptions } from '../selectors/erknmAnalyticsSelectors';
import styles from './ErknmAnalyticsPanel.module.scss';
import { erknmAnalyticsExport, erknmAnalyticsGetData, erknmAnalyticsSetFilter } from '../actions/erknmAnalyticsActions';
import { getDictionariesKnoOrganizationsSelectOptions } from '../../../CorePackage/DictionariesModule/selectors/dictionariesSelectors';
import { IKnoOrganizationDictionary } from '../../../APIPackage/DictionariesModule/interfaces/IKnoOrganizationDictionary';

interface IErknmAnalyticsPanel {
  analyticsTerritorialUnitsSelectOptions: IAnalyticsTerritorialUnitSelectOption[];
  filter: IErknmAnalyticsFilter | undefined;
  isSupervisor: boolean;
  knmKindOptions: Array<IDictionarySelectOption<IKnmKind>>;
  knoErvkSelectOptions: IDictionarySelectOption<IKnoOrganizationDictionary>[] | undefined;
  knoSelectOptions: IUserOrganisationSelectOption[] | undefined;
  prosecutorOrganizationsSelectOptions: IProsecutorOrganizationsSelectOption[];
  territorialUnitsSelectOptions: ITerritorialUnitSelectOption[];
}

const ErknmAnalyticsPanelWithoutStore = ({
  analyticsTerritorialUnitsSelectOptions,
  filter,
  isSupervisor,
  knmKindOptions,
  knoErvkSelectOptions,
  knoSelectOptions,
  prosecutorOrganizationsSelectOptions,
  territorialUnitsSelectOptions,
}: IErknmAnalyticsPanel) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();

  const handleExport = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      const { format } = event.currentTarget.dataset;

      dispatch(erknmAnalyticsExport(format as ExportFormatsEnum));
    },
    [dispatch],
  );

  const dropdownItems: IDropdownItem[] = useMemo(
    () => [
      {
        id: 'export-ods-analytics',
        content: (
          <DropdownItem onClick={handleExport} format={ExportFormatsEnum.ODS}>
            Экспорт списка в ODS
          </DropdownItem>
        ),
      },
      {
        id: 'export-xlsx-analytics',
        content: (
          <DropdownItem onClick={handleExport} format={ExportFormatsEnum.XLSX}>
            Экспорт списка в XLSX
          </DropdownItem>
        ),
      },
    ],
    [handleExport],
  );

  const handleFiltersFormSubmit = useCallback(
    (values: IErknmAnalyticsFilter) => {
      if (!isEmpty(values)) {
        dispatch(erknmAnalyticsSetFilter(values));
      }
      dispatch(erknmAnalyticsGetData());
    },
    [dispatch],
  );

  const validateForm = useCallback((values: IErknmAnalyticsFilter) => {
    const validateDatePicker = (fromDate: string | undefined, toDate: string | undefined, startDate?: boolean) => {
      if (
        toDate &&
        fromDate &&
        (!formatDate({ date: toDate || '', dateFormat: DATE_FORMAT_LONG_FNS }) ||
          !formatDate({ date: fromDate || '', dateFormat: DATE_FORMAT_LONG_FNS }))
      ) {
        return 'Некорректный период';
      }
      if (startDate && toDate && fromDate) {
        return makeNoLaterThenValidator(
          'Дата начала периода должна быть меньше или равна дате окончания',

          () => toDate,
        )(fromDate);
      }

      if (fromDate && toDate) {
        return makeNoSoonerThenValidator(
          'Дата окончания периода должна быть больше или равна дате начала',

          () => fromDate,
        )(toDate);
      }

      if ((toDate && !fromDate && startDate) || (fromDate && !toDate && !startDate)) return 'Поле не может быть пустым';
    };

    return {
      startDate: validateDatePicker(values?.startDate ?? undefined, values?.stopDate ?? undefined, true),
      stopDate: validateDatePicker(values?.startDate ?? undefined, values?.stopDate ?? undefined),
    };
  }, []);

  const renderFiltersForm = useCallback(
    ({ handleSubmit }: FormRenderProps<IErknmAnalyticsFilter>) => (
      <form onSubmit={handleSubmit} className={styles.FormContainer}>
        <Row notWrap>
          <div className={styles.FieldsContainer}>
            <Row notSpace>
              <FieldBlock label="Начало периода" size="col-4" color="black" margin="right">
                <DatePickerField name="startDate" />
              </FieldBlock>
              <FieldBlock label="Конец периода" size="col-4" color="black" margin="right">
                <DatePickerField name="stopDate" />
              </FieldBlock>
              <FieldBlock label="Вид мероприятия" size="col-4" color="black" margin="right">
                <SelectInputField name="knmKind" options={knmKindOptions} />
              </FieldBlock>
              <div className={styles.ColumnCellKno}>
                <FieldBlock label="КНО" color="black">
                  <SelectInputField name="kno" isMulti isSearchable options={knoErvkSelectOptions} />
                </FieldBlock>
                <FieldBlock>
                  <CheckboxField
                    className={styles.FilterFormGroupCheckbox}
                    id="withTerritoryAgencies"
                    name="withTerritoryAgencies"
                    label="Включая подчиненные тер. органы"
                    theme="system"
                    type="checkbox"
                  />
                </FieldBlock>
              </div>
            </Row>
            <Row notSpace>
              <div className={styles.ColumnCell}>
                <FieldBlock label="Тер. единицы" color="black" margin="right">
                  <SelectInputField
                    name="districts"
                    isMulti
                    isSearchable
                    options={analyticsTerritorialUnitsSelectOptions}
                  />
                </FieldBlock>
                <FieldBlock>
                  <CheckboxField
                    className={styles.FilterFormGroupCheckbox}
                    id="withInnerDistricts"
                    name="withInnerDistricts"
                    label="Включая подчиненные тер. единицы"
                    theme="system"
                    type="checkbox"
                  />
                </FieldBlock>
              </div>
              {!isSupervisor && (
                <div className={styles.ColumnCell}>
                  <FieldBlock label="Орган прокуратуры" color="black" margin="right">
                    <SelectInputField
                      name="prosecutorOrganizations"
                      isMulti
                      isSearchable
                      options={prosecutorOrganizationsSelectOptions}
                    />
                  </FieldBlock>
                  <FieldBlock>
                    <CheckboxField
                      className={styles.FilterFormGroupCheckbox}
                      id="withSubProsecutorOrganizations"
                      name="withSubProsecutorOrganizations"
                      label="Включая подчиненные органы прокуратуры"
                      theme="system"
                      type="checkbox"
                    />
                  </FieldBlock>
                </div>
              )}
            </Row>
          </div>
          <div className={styles.ButtonGroup}>
            <ModalActions padding="none">
              <Button type="submit" color="primary" size="large" text="Применить" />
              <Dropdown items={dropdownItems} />
            </ModalActions>
          </div>
        </Row>
      </form>
    ),
    [
      dropdownItems,
      knmKindOptions,
      knoSelectOptions,
      knoErvkSelectOptions,
      analyticsTerritorialUnitsSelectOptions,
      territorialUnitsSelectOptions,
      isSupervisor,
      prosecutorOrganizationsSelectOptions,
    ],
  );

  return (
    <Form<IErknmAnalyticsFilter>
      initialValues={filter}
      render={renderFiltersForm}
      onSubmit={handleFiltersFormSubmit}
      validate={validateForm}
    />
  );
};

const mapStateToProps = (state: IRootState) => ({
  analyticsTerritorialUnitsSelectOptions: getCatalogsAnalyticsTerritorialUnitsSelectOptions(state),
  filter: getErknmAnalyticsFilter(state),
  isSupervisor: getUserRoleIsSupervisor(state),
  knmKindOptions: getErknmAnalyticsKnmKindSelectOptions(state),
  knoErvkSelectOptions: getDictionariesKnoOrganizationsSelectOptions(state),
  knoSelectOptions: getAccountKnoOrganizationsSelectOptions(state),
  prosecutorOrganizationsSelectOptions: getCatalogsProsecutorOrganizationsSelectOptions(state),
  territorialUnitsSelectOptions: getCatalogsTerritorialUnitsSelectOptionsByLevelsCountryFederalDistrictRegions(state),
});

export const ErknmAnalyticsPanel = connect(mapStateToProps)(ErknmAnalyticsPanelWithoutStore);
